import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  ApiKey,
  NewApiKeyRequest,
  NewApiKeyResponse,
} from '@internal/backstage-plugin-api-keys-common';

export const apiKeysApiRef = createApiRef<ApiKeysApi>({
  id: 'plugin.api-keys.api',
});

export interface ApiKeysApi {
  listKeys(): Promise<ApiKey[]>;
  createKey(request: NewApiKeyRequest): Promise<NewApiKeyResponse>;
  deleteKey(id: number): Promise<void>;
}

export class ApiKeysClient implements ApiKeysApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async listKeys(): Promise<ApiKey[]> {
    return this.request('/keys');
  }

  async createKey(request: NewApiKeyRequest): Promise<NewApiKeyResponse> {
    return this.request('/keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  }

  async deleteKey(id: number): Promise<void> {
    await this.request(`/keys/${id}`, { method: 'DELETE' });
  }

  /** `fetchApi` attaches the caller's Backstage token itself. */
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('api-keys');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: { Accept: 'application/json', ...init?.headers },
    });

    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        body.message || `Request failed with status ${response.status}`,
      );
    }

    return response.status === 204 ? (undefined as T) : response.json();
  }
}
